import { Injectable, EventEmitter } from '@angular/core';
import { BcRestApi } from 'common/api/rest-api';
import { VisitsService } from 'common/visits/visits.service';
import { Subject, ReplaySubject } from 'rxjs';
import { AssistedBrowsingSession } from './assisted-browsing-session.interface';
import { StreamService } from 'common/dynamicquery/stream.service';
import { ActiveAssistService } from 'common/active-assist/active-assist.service';

@Injectable()
export class AssistedBrowsingService {
  private _sessionsByChatId = new Map();
  private _sessionsByPageViewId = new Map();
  private _messageListener;

  constructor(private _stream: StreamService,
              private _bcRestApi: BcRestApi,
              private _bcVisits: VisitsService,
              private _activeAssistService: ActiveAssistService) {
  }

  /*
   * Returns promise which will be resolved with session data, or will be rejected with error reason
   *
   */
  startSession(chatId: string, visitId: string, visitorId: string) {
    return this._getAssistedBrowsingId(chatId)
      .then((response) => {
        let assistedBrowsingId = response.id;
        if (!assistedBrowsingId) {
          throw 'Missing assistedBrowsingId';
        }
        return this._reviveSession(assistedBrowsingId, chatId, visitId, visitorId);
      })
      .catch(() => this._createSession(chatId, visitId, visitorId));
  }

  private _getAssistedBrowsingId (chatId: string) {
    return this._bcRestApi.getAssistedBrowsingId(chatId);
  }

  private _createSession(chatId: string, visitId: string, visitorId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const session: AssistedBrowsingSession = this._createSessionObject(resolve, reject, chatId, visitId, visitorId);
      this._sessionsByChatId.set(chatId, session);
      session.sessionInitiatedPromise = this._bcRestApi.beginAssistedBrowsingSession(chatId)
        .then((response) => {
          session.assistedBrowsingId = response.assistedBrowsingId;
        })
        .then (() => this._observeActiveAssist(session))
        .catch(() => this._cleanupSession(session));
    });
  }

  private _reviveSession(assistedBrowsingId: string, chatId: string, visitId: string, visitorId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const session: AssistedBrowsingSession = this._createSessionObject(resolve, reject, chatId, visitId, visitorId, assistedBrowsingId);
      this._sessionsByChatId.set(chatId, session);
      session.sessionInitiatedPromise = Promise.resolve();
      this._observeActiveAssist(session);
    });
  }

  syncPageView(chatId, pageViewId): Promise<any> {
    const session = this._sessionsByChatId.get(chatId);
    if (!session) {
      return Promise.reject('invalid chatId');
    }
    // eslint-disable-next-line rxjs/no-ignored-replay-buffer
    const channel = new ReplaySubject();
    this._registerListenerIfNeeded();
    this._sessionsByPageViewId.set(pageViewId, session);
    session.channelsByPageViewId.set(pageViewId, channel);
    return this._bcRestApi.startAssistedBrowsingSynchronization(session.assistedBrowsingId, session.visitId, pageViewId)
      .then((response) => {
        return { url: response.iframeUrl, messageObservable: channel };
      });
  }

  /*
   * Stops initiated session by operator
   */
  stopSession(chatId: string): Promise<any> {
    const session = this._sessionsByChatId.get(chatId);
    if (!session) {
      throw new Error('Missing session');
    }
    return session.sessionInitiatedPromise
      .then(() => this._stopSession(session));
  }

  private _stopSession(session: AssistedBrowsingSession): Promise<any> {
    return this._bcRestApi.endAssistedBrowsingSession(session.assistedBrowsingId);
  }

  pushPage(chatId: string, pageViewId: string, url: string) {
    const session = this._sessionsByChatId.get(chatId);

    this._verifyPageViewId(session, pageViewId);

    return this._bcRestApi.pushAssistedBrowsingPage(session.assistedBrowsingId, session.visitId, pageViewId, url);
  }

  navigateBack(chatId: string, pageViewId: string) {
    const session = this._sessionsByChatId.get(chatId);

    this._verifyPageViewId(session, pageViewId);

    return this._bcRestApi.navigateAssistedBrowsingBack(session.assistedBrowsingId, session.visitId, pageViewId);
  }

  navigateForward(chatId: string, pageViewId: string) {
    const session = this._sessionsByChatId.get(chatId);

    this._verifyPageViewId(session, pageViewId);

    return this._bcRestApi.navigateAssistedBrowsingForward(session.assistedBrowsingId, session.visitId, pageViewId);
  }

  reloadPage(chatId: string, pageViewId: string) {
    const session = this._sessionsByChatId.get(chatId);

    this._verifyPageViewId(session, pageViewId);

    return this._bcRestApi.reloadAssistedBrowsingPage(session.assistedBrowsingId, session.visitId, pageViewId);
  }

  private _verifyPageViewId(session: AssistedBrowsingSession, pageViewId: string) {
    if (!session) {
      throw new Error('Missing session');
    }
    const channel = session.channelsByPageViewId.get(pageViewId);
    if (!channel) {
      throw new Error(`Invalid pageViewId ${pageViewId}`);
    }
  }

  getBookmarks() {
    return this._bcRestApi.getBookmarks();
  }

  private _cleanupSession(session: AssistedBrowsingSession) {
    if (session) {
      if (!session.isStarted) {
        session.sessionFailed('stopped');
      } else {
        session.closeEvent.emit('stopped');
      }
      if (session.activeAssistSubscription) {
        session.activeAssistSubscription.unsubscribe();
      }
      for (let id of Array.from(session.channelsByPageViewId.keys())) {
        this._sessionsByPageViewId.delete(id);
      }
      this._sessionsByChatId.delete(session.chatId);
      session.isStarted = false;
    }
    this._unregisterListenerIfNeeded();
  }

  private _observeActiveAssist(session) {
    session.activeAssistSubscription = this._activeAssistService.createObservableByActiveAssistId(session.assistedBrowsingId).subscribe((event) => this._onActiveAssistChange(event, session));
  }

  private _onActiveAssistChange(event, session) {
    if (!event.data) {
      return;
    }
    if (!event.data.Ended && event.data.Started && !session.isStarted) {
      this._observeVisit(session);
    }
    if (event.data.Ended) {
      this._cleanupSession(session);
    }
  }

  private _observeVisit(session) {
    const visitObservable = this._bcVisits.createObservableByVisitorId(session.visitId, session.visitorId, new Date());
    session.closeEvent = new EventEmitter();
    session.sessionStarted({ pageViewObservable: visitObservable, closeEvent: session.closeEvent });
    session.isStarted = true;
  }

  private _registerListenerIfNeeded() {
    if (!this._messageListener) {
      this._messageListener = (params, data) => this._messageHandler(params, data);
      this._stream.registerMessageHandler('message', this._messageListener);
    }
  }

  private _unregisterListenerIfNeeded() {
    if (this._sessionsByChatId.size === 0 && this._messageListener) {
      this._messageListener = null;
      this._stream.unregisterMessageHandler('message');
    }
  }

  private _messageHandler(params, data) {
    const parts = data.params[0].split('-');
    if (parts.length === 2) {
      const method = parts[1];
      const pageViewId = data.params[1].PageViewID;
      const channel = this._sessionsByPageViewId.get(pageViewId);
      if (channel) {
        channel.channelsByPageViewId.get(pageViewId).next({ method, params: data.params[1][method] });
      }
    }
  }

  private _createSessionObject(resolve, reject, chatId, visitId, visitorId, assistedBrowsingId = null): AssistedBrowsingSession {
    return {
      isStarted: false,
      sessionStarted: resolve,
      sessionFailed: reject,
      chatId,
      visitId,
      visitorId,
      assistedBrowsingId,
      channelsByPageViewId: new Map<string, Subject<any>>(),
      activeAssistSubscription: null,
      closeEvent: null,
      sessionInitiatedPromise: null
    };
  }
}
